import { useEffect } from 'react';
import { create } from 'zustand';
import { useAuthStore } from './auth';
import { sandbox } from './sandbox';

/**
 * `rainmaker_users.must_change_password` — the flag, and the one place the app reads it.
 *
 * 🔴 WHY THIS FILE EXISTS (Ravi, 29 Sep 2026). InviteUser.tsx and TeamUsers.tsx have both been
 * WRITING this flag since the invite screen was built, and NOTHING in the codebase read it back —
 * grepped, twice. So every person invited to Rainmaker has been carrying the password an
 * administrator typed for them, indefinitely, and the promise printed on the invite screen
 * ("They will be prompted to change it on first login") was never kept by any code. Six people on
 * live sign in with addresses on `rainmkr.local`, a domain that does not exist, so the emailed
 * reset path cannot reach them either. Reading the flag is what turns the invite screen's promise
 * into something that actually happens.
 *
 * It is a UI nudge, NOT a security control. What a signed-in person may read or write is decided
 * by RLS at the database and does not consult this flag — which is exactly why the failure
 * behaviour below is the right way round.
 *
 * 🔴 FAIL OPEN, DELIBERATELY. If the read fails or the column is missing we report 'clear' and
 * nobody is forced anywhere. The opposite posture would mean a dropped request on a bad line
 * pins a salesperson to a change-password screen he was never meant to see, with no way past it
 * and his day's work behind it. Denying a person their own app to enforce a reminder is a worse
 * outcome than the reminder being missed, and RLS loses nothing either way. (Contrast
 * session.tsx, which fails CLOSED — there the flag decides what you may SEE, so the least is the
 * safe answer. Here it decides where you must GO, and the least is to leave you alone.)
 */
export type PasswordFlagStatus =
  | 'unknown'   // not looked up yet (or signed out) — enforce nothing
  | 'required'  // the flag is set: this person must change their password before going on
  | 'clear';    // the flag is not set, or we could not read it (see fail-open note above)

interface FlagState {
  status: PasswordFlagStatus;
  /** The user id the current `status` belongs to, so a different sign-in re-reads. */
  checkedFor: string | null;
  check: (userId: string) => void;
  /** Called by the change-password screen the moment the password actually changed. */
  markChanged: () => void;
  reset: () => void;
}

export const usePasswordFlagStore = create<FlagState>((set, get) => ({
  status: 'unknown',
  checkedFor: null,
  check: (userId) => {
    // One read per signed-in person per page load. The flag only changes when this person
    // changes their own password (markChanged below) or an administrator resets it, and the
    // latter takes effect at their next sign-in, so polling would buy nothing.
    if (get().checkedFor === userId) return;
    set({ checkedFor: userId, status: 'unknown' });
    sandbox<{ must_change_password: boolean | null }>(
      'rainmaker_users',
      `id=eq.${userId}&select=must_change_password`,
    )
      .then((rows) => {
        // No row, a null, or anything other than an explicit true means "nothing to enforce".
        set({ status: rows[0]?.must_change_password === true ? 'required' : 'clear' });
      })
      .catch(() => set({ status: 'clear' })); // fail open — see the header note
  },
  markChanged: () => set({ status: 'clear' }),
  reset: () => set({ status: 'unknown', checkedFor: null }),
}));

/**
 * The flag for whoever is signed in right now. Returns 'unknown' when signed out, so a guard
 * written against this can never fire on the login screen.
 *
 * `rainmaker_users.id` is the right key here: Login.tsx stores that id on the auth user (the
 * auth_user_id → id lookup happens there), and `must_change_password` lives on that same row.
 */
export function useMustChangePassword(): PasswordFlagStatus {
  const userId = useAuthStore((s) => s.user?.id) ?? null;
  const status = usePasswordFlagStore((s) => s.status);
  const check = usePasswordFlagStore((s) => s.check);
  const reset = usePasswordFlagStore((s) => s.reset);

  useEffect(() => {
    if (userId) check(userId);
    else reset(); // signing out must forget the answer, or the next person inherits it
  }, [userId, check, reset]);

  return userId ? status : 'unknown';
}
